import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useMemo } from "react";
import {
  faSort,
  faSortUp,
  faSortDown,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import { getOrderHistory } from "../../../../services/order.service";
import { OrderHistoryItem, SortConfig, SortField } from "../../../../types/order.types";
import { formatDate, formatNumber } from "../../../../utils/helper";
import { useDarkMode } from "../../../../recoil";

interface OrderHistoryProps {
  isActive: boolean;
  market?: string;
}

const OrderHistory = ({ isActive, market }: OrderHistoryProps) => {
  const [darkMode, setDarkMode] = useDarkMode();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: "date",
    direction: "desc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Show 10 rows per page

  // SWR fetch for order history
  const { data: orderHistoryResponse, error, isValidating } = useSWR(
    ["getOrderHistory", market, "limit"],
    () => getOrderHistory(market, "limit"),
    {
      errorRetryInterval: 3000,
      errorRetryCount: 0,
      revalidateOnFocus: false,
    }
  );

  const orderHistory: OrderHistoryItem[] = orderHistoryResponse?.items || [];
  const isLoading = !orderHistoryResponse && !error;

  const handleSort = (field: SortField) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (field: SortField) => {
    if (sortConfig.field !== field) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  // Apply sorting
  const processedOrders = useMemo(() => {
    const sorted = [...orderHistory].sort((a, b) => {
      const { field, direction } = sortConfig;
      let compare = 0;

      switch (field) {
        case "date":
          compare = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "price":
          compare = parseFloat(a.price) - parseFloat(b.price);
          break;
        case "amount":
          compare = parseFloat(a.size) - parseFloat(b.size);
          break;
        case "side":
          compare = a.side.localeCompare(b.side);
          break;
        case "type":
          compare = a.type.localeCompare(b.type);
          break;
        default:
          compare = 0;
      }
      return direction === "asc" ? compare : -compare;
    });
    return sorted;
  }, [orderHistory, sortConfig]);

  // Calculate pagination
  const totalItems = processedOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = processedOrders.slice(startIndex, endIndex);



  if (!isActive) return null;

  return (
    <div className={`h-full  ${darkMode ? 'custom_table_container' : 'custom_table_container_light'} text-[#121212] dark:!text-white`}>
      <div className="table-responsive">
        <table className={`table  custom_table table-hover ${darkMode ? 'table-dark' : 'table-light'}`}>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort("date")}>
                Date <FontAwesomeIcon icon={getSortIcon("date")} className="ml-1" />
              </th>
              <th>Pair</th>
              <th className="cursor-pointer" onClick={() => handleSort("type")}>
                Type <FontAwesomeIcon icon={getSortIcon("type")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("side")}>
                Side <FontAwesomeIcon icon={getSortIcon("side")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("price")}>
                Price <FontAwesomeIcon icon={getSortIcon("price")} className="ml-1" />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("amount")}>
                Amount <FontAwesomeIcon icon={getSortIcon("amount")} className="ml-1" />
              </th>
              <th>Fee</th>
              <th>Liquidity</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="text-center text-gray-400 py-4">
                  Loading order history...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={9} className="text-center text-red-400 py-4">
                  Error loading order history. Please try again.
                </td>
              </tr>
            ) : currentPageData.length > 0 ? (
              currentPageData.map((order) => (
                <tr key={order.tradeId} className="hover:bg-gray-800 transition-colors">
                  <td>{formatDate(new Date(order.createdAt).getTime())}</td>
                  <td className="flex justify-start items-center"> <img
                    src={`https://assets.coincap.io/assets/icons/${(
                      order?.symbol?.split('-')[1] ||
                      order?.symbol?.split('/')[0] || // Handle BTC/USDT format
                      'btc'
                    ).toLowerCase()}@2x.png`}
                    alt=""
                    className="mr-2 h-[20px] w-[20px] rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://via.placeholder.com/20?text=C'; // Fallback to a placeholder
                      e.currentTarget.style.display = 'block'; // Ensure it's visible
                    }}
                  />{order.symbol}</td>
                  <td className="text-gray-300">{order.type.toUpperCase()}</td>
                  <td className={order.side === "buy" ? "text-up" : "text-down"}>
                    {order.side.toUpperCase()}
                  </td>
                  <td>{formatNumber(order.price)}</td>
                  <td>{formatNumber(order.size)}</td>
                  <td className="text-gray-400">
                    {formatNumber(order.fee)} {order.feeCurrency}
                  </td>
                  <td className="text-gray-400 capitalize">
                    {order.liquidity}
                  </td>
                  <td className="text-green-400">
                    Filled
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={9} className="text-center text-gray-400 py-4">
                  No order history found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls - Client-side */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4 px-4">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-700 dark:text-white rounded disabled:opacity-50 hover:bg-gray-600 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm dark:text-gray-300">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 bg-gray-700 dark:text-white rounded disabled:opacity-50 hover:bg-gray-600 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;